/**
 * Axios API client with Cognito auth interceptors.
 * Uses getAuthToken/clearAuthToken/setAuthToken from @amzn/neura-web-shared-components.
 * AuthProvider populates the token store after fetchAuthSession() succeeds.
 *
 * On 401, the interceptor refreshes the Cognito session via Amplify and retries
 * the original request once. If the refresh fails, the token is cleared and the
 * user will be redirected to sign-in by AuthProvider/ProtectedRoute.
 */

import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getAuthToken, clearAuthToken, setAuthToken } from '@amzn/neura-web-shared-components';

export const apiClient = axios.create({
  baseURL: '', // Set at runtime via setApiBaseUrl() after config.json loads
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

/** Called from bootstrap() once runtime config is available */
export function setApiBaseUrl(url: string): void {
  apiClient.defaults.baseURL = url;
}

let _chatUrl = '';

/** Called from bootstrap() once runtime config is available */
export function setChatUrl(url: string): void {
  _chatUrl = url;
}

/** Returns the chat app URL for the current environment */
export function getChatUrl(): string {
  return _chatUrl;
}

// Request interceptor — inject Cognito ID token
apiClient.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Track whether a token refresh is already in flight to avoid concurrent refreshes
let refreshPromise: Promise<string | null> | null = null;

async function refreshToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession({ forceRefresh: true });
    const idToken = session.tokens?.idToken?.toString() ?? null;
    if (idToken) {
      setAuthToken(idToken);
    } else {
      clearAuthToken();
    }
    return idToken;
  } catch {
    clearAuthToken();
    return null;
  }
}

// Response interceptor — on 401, refresh token and retry once
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retried?: boolean };

    if (error.response?.status === 401 && originalRequest && !originalRequest._retried) {
      originalRequest._retried = true;

      // Coalesce concurrent refreshes into a single Amplify call
      if (!refreshPromise) {
        refreshPromise = refreshToken().finally(() => { refreshPromise = null; });
      }
      const newToken = await refreshPromise;

      if (newToken) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      }

      // Refresh failed — clear token, AuthProvider will redirect to sign-in
      clearAuthToken();
    }

    return Promise.reject(error);
  }
);

export default apiClient;
